import { getSharedStyleSheet } from './sharedStyleSheet.js';

class CheckBox extends HTMLElement {
    static observedAttributes = ['id', 'name', 'text', 'small', 'checked', 'disabled'];

    // Identify the element as a form-associated custom element
    static formAssociated = true;

    constructor() {
        super();
        this._onCheckedHandler = null;
    }

    attributeChangedCallback(attrName, oldValue, newValue) {
        this.dynamicRender();
    }

    connectedCallback() {
        if (!this.shadowRoot) {
            this.attachShadow({mode: 'open', delegatesFocus: true});

            this.shadowRoot.host.style.display = 'inline-block';

            // Share a single parsed stylesheet across all shadow roots
            getSharedStyleSheet().then(sheet => {
                this.shadowRoot.adoptedStyleSheets = [sheet];
            });

            this._wrapper = document.createElement('div');
            this._wrapper.className = 'checkbox-wrapper';
            this._wrapper.tabIndex = 0;
            this.shadowRoot.appendChild(this._wrapper);

            const square = `<div class="checkbox-square"` + (!!this.getAttribute('checked') ? ' checked="checked"' : '') + `></div>`;

            this._wrapper.insertAdjacentHTML('beforeend', square);
            this._square = this._wrapper.querySelector('.checkbox-square');

            const text = `<span class="checkbox-text">` + this.getAttribute('text') + `</span>`;

            this._wrapper.insertAdjacentHTML('beforeend', text);
            this._text = this._wrapper.querySelector('.checkbox-text');

            // Get access to the internal form control APIs
            this._internals = this.attachInternals();

            this.setupEvents();
        }

        this.dynamicRender();
    }

    setupEvents() {
        this._wrapper.addEventListener('keydown', (e) => {
            if (e.key === ' ') {
                e.preventDefault();
                this.handleClick();
            }
        });

        this._square.addEventListener('click', (e) => {
            this.handleClick();
        });

        this._text.addEventListener('click', (e) => {
            this.handleClick();
        });
    }

    handleClick() {
        if (!!this.getAttribute('disabled')) return;

        if (!!this.getAttribute('checked')) {
            this.removeAttribute('checked');
            this._square.classList.remove('checked');
        } else {
            this.setAttribute('checked', 'checked');
            this._square.classList.add('checked');
        }

        this.updateFormValue();

        // Trigger change event
        this.shadowRoot.dispatchEvent(new Event('change', {
            composed: true,
            bubbles: true
        }));

        this.shadowRoot.dispatchEvent(new Event('checked', {
            composed: true,
            bubbles: true
        }));

        if (this._onCheckedHandler) {
            this._onCheckedHandler();
        }
    }

    dynamicRender() {
        if (!this._wrapper || !this._square || !this._text) return;

        this._text.innerHTML = this.getAttribute('text');

        if (!!this.getAttribute('checked')) {
            this._square.classList.add('checked');
        } else {
            this._square.classList.remove('checked');
        }

        this.updateFormValue();

        if (!!this.getAttribute('small')) {
            this._square.classList.add('small');
            this._text.classList.add('small');
        } else {
            this._square.classList.remove('small');
            this._text.classList.remove('small');
        }

        if (!!this.getAttribute('disabled')) {
            this._square.classList.add('disabled');
        } else {
            this._square.classList.remove('disabled');
        }
    }

    updateFormValue() {
        if ('undefined' === typeof this._internals) return;

        if (!!this.getAttribute('data-value')) {
            this._internals.setFormValue((!!this.getAttribute('checked') ? this.getAttribute('data-value') : null));
        } else {
            this._internals.setFormValue((!!this.getAttribute('checked') ? 'on' : null));
        }
    }

    get onchecked()
    {
        return this._onCheckedHandler;
    }

    set onchecked(f)
    {
        this._onCheckedHandler = f;
    }

    get checked()
    {
        return !!this.getAttribute('checked');
    }

    set checked(c)
    {
        if (c) {
            this.setAttribute('checked', 'checked');
        } else {
            this.removeAttribute('checked');
        }

        this.dynamicRender();
    }

    // Form controls usually expose a "value" property
    get value() {
        return (!!this.getAttribute('checked') ? 'on' : '');
    }

    set value(v) {
        this.setAttribute('checked', (v === 'on' ? 'checked' : ''));
    }

    // The following properties and methods aren't strictly required,
    // but browser-level form controls provide them. Providing them helps
    // ensure consistency with browser-provided controls.
    get form() {
        return this._internals.form;
    }

    get name() {
        return this.getAttribute('name');
    }

    get type() {
        return this.localName;
    }

    get validity() {
        return this._internals.validity;
    }

    get validationMessage() {
        return this._internals.validationMessage;
    }

    get willValidate() {
        return this._internals.willValidate;
    }

    checkValidity() {
        return this._internals.checkValidity();
    }

    reportValidity() {
        return this._internals.reportValidity();
    }
}

customElements.define('check-box', CheckBox);

